import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Per-section error boundary so one broken section never blanks the whole page.
 *
 * CRITICAL — the boundary alone is NOT enough on the server. Under React 18's streaming SSR
 * (`renderToPipeableStream`, which is what @astrojs/react drives), `getDerivedStateFromError` is NEVER
 * called: a throw inside the shell goes straight to `onShellError` and the emitted HTML is the EMPTY
 * STRING. Astro has already written `writeHead(200)` by then, so the visitor gets a 200 with a blank
 * document — one broken section killed the entire store page. Verified empirically against the exact
 * react-dom (18.3.1) this repo resolves.
 *
 * The fix is the `<Suspense>` below. Fizz CAN recover an errored Suspense boundary: it emits the fallback
 * in place of that subtree and lets the rest of the document stream normally. So the class component keeps
 * catching CLIENT-side errors (the builder canvas, hydration), and Suspense contains SERVER-side ones.
 * Both layers are required; neither replaces the other.
 *
 * The fallback is deliberately `null` and NOT the edit-mode error notice: a Suspense fallback also renders
 * during a legitimate suspension (a future `React.lazy` section), and flashing "this section failed" at a
 * section that is merely loading would be a lie.
 */
import { t } from '@aquilixshop/i18n';
import { Component, Suspense } from 'react';
export class SectionErrorBoundary extends Component {
    state = { hasError: false };
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (!this.state.hasError)
            return _jsx(Suspense, { fallback: null, children: this.props.children });
        if (this.props.mode === 'live')
            return null;
        return (_jsx("div", { "data-aqx-section-error": "", style: {
                paddingBlock: 'var(--aqx-sp-4)',
                paddingInline: 'var(--aqx-sp-6)',
                border: '1px dashed var(--aqx-danger)',
                borderRadius: 'var(--aqx-r-md)',
                color: 'var(--aqx-danger)',
                fontFamily: 'var(--aqx-font-sans)',
                fontSize: 'var(--aqx-text-sm)',
            }, children: t(null, 'error.section_failed') }));
    }
}
//# sourceMappingURL=SectionErrorBoundary.js.map